import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  Play,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  FileText,
  BarChart3,
  User,
  Bot,
  Phone,
  AlertTriangle
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';

const QATestRunPage = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedExecution, setExpandedExecution] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [runRes, reportRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/qa/runs/${runId}`, { headers }),
        fetch(`${API_BASE_URL}/api/qa/runs/${runId}/report`, { headers })
      ]);

      if (runRes.ok) {
        const data = await runRes.json();
        setRun(data.run);
      }
      if (reportRes.ok) {
        const data = await reportRes.json();
        setReport(data.report);
      }
    } catch (error) {
      console.error('Failed to fetch run data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Poll for updates if run is still running
    const interval = setInterval(() => {
      if (run?.status === 'running') {
        fetchData();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [runId, run?.status]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'running':
      case 'in_progress':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-gray-400" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      running: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-blue-100 text-blue-800',
      pending: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-yellow-100 text-yellow-800'
    };
    return styles[status] || styles.pending;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!run) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
        <p className="text-gray-600">Test run not found</p>
        <Link to="/app/qa" className="text-blue-600 hover:underline mt-2 inline-block">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/app/qa')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Test Run: {run.testSuite?.name || 'Unknown Suite'}
            </h1>
            <p className="text-gray-600">
              Started {new Date(run.startedAt || run.createdAt).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span className={clsx('px-3 py-1 rounded-full text-sm font-medium', getStatusBadge(run.status))}>
            {run.status}
          </span>
          <button
            onClick={fetchData}
            className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Overall Score</p>
            <p className={clsx('text-2xl font-bold', getScoreColor(report.averageScores?.overall || 0))}>
              {report.averageScores?.overall || '--'}%
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Pass Rate</p>
            <p className={clsx('text-2xl font-bold', getScoreColor(report.summary?.passRate || 0))}>
              {report.summary?.passRate || '--'}%
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Passed</p>
            <p className="text-2xl font-bold text-green-600">{report.summary?.passed || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Failed</p>
            <p className="text-2xl font-bold text-red-600">{report.summary?.failed || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Avg Duration</p>
            <p className="text-2xl font-bold text-gray-900">{formatDuration(report.summary?.avgDuration)}</p>
          </div>
        </div>
      )}

      {/* Score Breakdown */}
      {report?.averageScores && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Score Breakdown</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {['accuracy', 'helpfulness', 'tone', 'responseTime'].map((metric) => (
              <div key={metric} className="text-center">
                <div className={clsx(
                  'inline-flex items-center justify-center w-16 h-16 rounded-full mb-2',
                  getScoreBg(report.averageScores[metric] || 0)
                )}>
                  <span className={clsx('text-xl font-bold', getScoreColor(report.averageScores[metric] || 0))}>
                    {report.averageScores[metric] || '--'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 capitalize">{metric.replace(/([A-Z])/g, ' $1').trim()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Executions List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Test Executions</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {run.executions?.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No executions yet
            </div>
          ) : (
            run.executions?.map((execution) => (
              <div key={execution.id} className="hover:bg-gray-50">
                {/* Execution Header */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedExecution(
                    expandedExecution === execution.id ? null : execution.id
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {getStatusIcon(execution.status)}
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {execution.testScenario?.name || 'Unknown Scenario'}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {execution.testScenario?.personaType || 'customer'} persona
                          {execution.durationSeconds && ` • ${formatDuration(execution.durationSeconds)}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {execution.overallScore && (
                        <span className={clsx(
                          'px-3 py-1 rounded-full text-sm font-medium',
                          getScoreBg(Number(execution.overallScore)),
                          getScoreColor(Number(execution.overallScore))
                        )}>
                          {execution.overallScore}%
                        </span>
                      )}
                      {execution.passed !== null && (
                        execution.passed ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                            PASSED
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                            FAILED
                          </span>
                        )
                      )}
                      {expandedExecution === execution.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedExecution === execution.id && (
                  <div className="px-4 pb-4 space-y-4">
                    {/* Failure Reason */}
                    {execution.failureReason && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm font-medium text-red-800">Failure Reason:</p>
                        <p className="text-sm text-red-700">{execution.failureReason}</p>
                      </div>
                    )}

                    {/* Scores Grid */}
                    {(execution.accuracyScore || execution.helpfulnessScore || execution.toneScore) && (
                      <div className="grid grid-cols-4 gap-4">
                        <div className="p-3 bg-gray-50 rounded-lg text-center">
                          <p className="text-xs text-gray-500">Accuracy</p>
                          <p className={clsx('text-lg font-bold', getScoreColor(Number(execution.accuracyScore) || 0))}>
                            {execution.accuracyScore || '--'}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg text-center">
                          <p className="text-xs text-gray-500">Helpfulness</p>
                          <p className={clsx('text-lg font-bold', getScoreColor(Number(execution.helpfulnessScore) || 0))}>
                            {execution.helpfulnessScore || '--'}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg text-center">
                          <p className="text-xs text-gray-500">Tone</p>
                          <p className={clsx('text-lg font-bold', getScoreColor(Number(execution.toneScore) || 0))}>
                            {execution.toneScore || '--'}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg text-center">
                          <p className="text-xs text-gray-500">Transfer</p>
                          <p className="text-lg font-bold">
                            {execution.transferSuccess === null ? '--' :
                             execution.transferSuccess ? '✓' : '✗'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* AI Evaluation */}
                    {execution.aiEvaluation && (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                          <BarChart3 className="w-4 h-4 mr-2" />
                          AI Evaluation
                        </h4>
                        <div className="space-y-2 text-sm text-blue-900">
                          {execution.aiEvaluation.strengths && (
                            <div>
                              <span className="font-medium">Strengths: </span>
                              {Array.isArray(execution.aiEvaluation.strengths)
                                ? execution.aiEvaluation.strengths.join(', ')
                                : execution.aiEvaluation.strengths}
                            </div>
                          )}
                          {execution.aiEvaluation.weaknesses && (
                            <div>
                              <span className="font-medium">Areas for Improvement: </span>
                              {Array.isArray(execution.aiEvaluation.weaknesses)
                                ? execution.aiEvaluation.weaknesses.join(', ')
                                : execution.aiEvaluation.weaknesses}
                            </div>
                          )}
                          {execution.aiEvaluation.summary && (
                            <div>
                              <span className="font-medium">Summary: </span>
                              {execution.aiEvaluation.summary}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Keyword Matches */}
                    {execution.keywordMatches && Object.keys(execution.keywordMatches).length > 0 && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Keyword Matches</h4>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(execution.keywordMatches).map(([keyword, matched]) => (
                            <span
                              key={keyword}
                              className={clsx(
                                'px-2 py-1 text-xs rounded',
                                matched
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              )}
                            >
                              {matched ? '✓' : '✗'} {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Transcript */}
                    {execution.mergedTranscript && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
                          <h4 className="text-sm font-medium text-gray-700 flex items-center">
                            <FileText className="w-4 h-4 mr-2" />
                            Conversation Transcript
                          </h4>
                        </div>
                        <div className="p-4 max-h-96 overflow-y-auto space-y-3">
                          {Array.isArray(execution.mergedTranscript) ? (
                            execution.mergedTranscript.map((turn, idx) => (
                              <div
                                key={idx}
                                className={clsx(
                                  'flex items-start space-x-3',
                                  turn.role === 'tester' ? 'justify-start' : 'justify-end'
                                )}
                              >
                                {turn.role === 'tester' && (
                                  <div className="p-2 bg-purple-100 rounded-full">
                                    <User className="w-4 h-4 text-purple-600" />
                                  </div>
                                )}
                                <div
                                  className={clsx(
                                    'max-w-[70%] p-3 rounded-lg',
                                    turn.role === 'tester'
                                      ? 'bg-purple-50 text-purple-900'
                                      : 'bg-blue-50 text-blue-900'
                                  )}
                                >
                                  <p className="text-xs font-medium mb-1">
                                    {turn.role === 'tester' ? 'AI Tester' : 'Target AI'}
                                  </p>
                                  <p className="text-sm">{turn.text}</p>
                                  {turn.timestamp && (
                                    <p className="text-xs text-gray-400 mt-1">
                                      {new Date(turn.timestamp).toLocaleTimeString()}
                                    </p>
                                  )}
                                </div>
                                {turn.role !== 'tester' && (
                                  <div className="p-2 bg-blue-100 rounded-full">
                                    <Bot className="w-4 h-4 text-blue-600" />
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                              {JSON.stringify(execution.mergedTranscript, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Recording Link */}
                    {execution.recordingUrl && (
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <a
                          href={execution.recordingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-sm"
                        >
                          Listen to Recording
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default QATestRunPage;
